"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Facebook,
} from "lucide-react";
import Image from "next/image";

export const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About Us" },
  { href: "/services", label: "Services" },
  { href: "/projects", label: "Projects" },
  { href: "/team", label: "Our Team" },
  { href: "/contact", label: "Contact" },
];

interface SiteSettings {
  footer_logo: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  twitter_url: string;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  copy_right: string;
}

interface Service {
  id: number;
  name: string;
  slug: string;
}

interface FooterProps {
  initialSettings?: SiteSettings | null;
  initialServices?: Service[];
}

const Footer: React.FC<FooterProps> = ({ initialSettings, initialServices }) => {
  const [settings, setSettings] = useState<SiteSettings | null>(initialSettings || null);
  const [services, setServices] = useState<Service[]>(initialServices || []);

  /* =============================
     FETCH SITE SETTINGS
     ============================= */
  useEffect(() => {
    if (settings) return; // Skip if provided by props

    const fetchSettings = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/settings", {
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const data = await res.json();
        if (data.success) {
          setSettings(data.data);
        }
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };

    fetchSettings();
  }, [settings]);

  /* =============================
     FETCH SERVICES (ASC ORDER)
     ============================= */
  useEffect(() => {
    if (services && services.length > 0) return; // Skip if provided by props

    const fetchServices = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/services");
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const data = await res.json();
        if (data.success && data.data) {
          const sorted = [...data.data].sort(
            (a: Service, b: Service) => a.id - b.id,
          );
          setServices(sorted);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServices();
  }, [services]);

  const createSlug = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  return (
    <footer className="bg-[#2a6380] text-white">
      {/* Main Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Logo & Description */}
        <div>
          <Link href="/" className="block mb-4">
            <Image
              src="/Renvixx-Logo1.png"
              alt="Renvixx Logo"
              width={250}
              height={50}
              className="object-contain"
            />
          </Link>
          <p className="text-sm text-gray-300 leading-relaxed text-justify mb-2">
            Leading construction and consulting company dedicated to building
            tomorrow’s infrastructure with innovative solutions, quality
            craftsmanship, and unwavering commitment to excellence.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed text-justify">
            Renvixx is a brand owned and operated by NAM ENTERPRISES (SMC-PRIVATE) LIMITED.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="text-lg font-semibold text-[#FFF] mb-4">
            Quick Links
          </h3>
          <ul className="space-y-2 text-sm text-gray-200">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="transition-colors duration-300 hover:text-[#33c1c7]"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Services (FROM API) */}
        <div>
          <h3 className="text-lg font-semibold text-[#FFF] mb-4">Services</h3>
          <ul className="space-y-2 text-sm text-gray-200">
            {services.map((service) => {
              const slug = service.slug || createSlug(service.name);
              return (
                <li key={service.id}>
                  <Link
                    href={`/services/${slug}`}
                    className="hover:text-[#33c1c7] transition"
                  >
                    {service.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h3 className="text-lg font-semibold text-[#FFF] mb-4">Find Us</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2">
              <Mail className="text-[#FFF] w-4 h-4" />
              <Link href={`mailto:${settings?.contact_email}`}>
                {settings?.contact_email}
              </Link>
            </li>

            <li className="flex items-center gap-2">
              <Phone className="text-[#FFF] w-4 h-4" />
              <Link href={`tel:${settings?.contact_phone}`}>
                {settings?.contact_phone}
              </Link>
            </li>

            <li className="flex items-start gap-2">
              <MapPin className="text-[#FFF] w-8 h-4 mt-1" />
              <span>{settings?.contact_address}</span>
            </li>
          </ul>

          {/* Social Links */}
          <div className="flex justify-center gap-4 mt-4">
            <Link href={settings?.twitter_url || "#"} aria-label="Twitter">
              <Twitter size={18} />
            </Link>
            <Link href={settings?.linkedin_url || "#"} aria-label="LinkedIn">
              <Linkedin size={18} />
            </Link>
            <Link href={settings?.instagram_url || "#"} aria-label="Instagram">
              <Instagram size={18} />
            </Link>
            <Link href={settings?.facebook_url || "#"} aria-label="Facebook">
              <Facebook size={18} />
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#2a6380] text-[#FFF] border-t border-[#FFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between py-3">
          <p>© 2026 NAM ENTERPRISES (SMC-PRIVATE) LIMITED. All rights reserved.</p>
          <div className="flex gap-4">
            <Link href="/privacy-policy">Privacy Policy</Link>
            <Link href="/terms-of-service">Terms of Service</Link>
            <Link href="/cookie-policy">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
